import {
  type CatalogEntry,
  catalogEntries,
  VARIANT_AXES,
  type VariantTags,
} from "@/platform/thecore/ingest";

// The catalog query seam: faceting and route resolution over the
// published Presets. v1 is backed by the vendored catalogEntries; the functions
// are pure and take their entries explicitly, so a backend source can feed the
// same shape later without touching callers. They are generic over the fields
// they read (variant / routePath), which also keeps them testable with light
// fixtures. Re-exported here so new code treats `../catalog` as the seam boundary.
export { type CatalogEntry, catalogEntries, type VariantTags };

/** One Variant axis: edition, the universal hand, or a per-edition race / size / modifier placement. */
export type AxisKey = (typeof VARIANT_AXES)[number];

/** The Variant-tag dimensions faceting offers, in canonical positional order (TheCore-owned). */
export const DIMENSIONS: readonly AxisKey[] = VARIANT_AXES;

/** Axis-value tokens that need a display label other than a plain Title-case. 1.0's three non-Zerg
 * arrangements ship as one byte-identical file presented honestly as "P/T/R". */
const VALUE_LABELS: Record<string, string> = { ptr: "P/T/R" };

/** The display label for one axis value: a special case (P/T/R), else Title-cased (Zerg, Standard). */
export function axisOptionLabel(_axis: AxisKey, value: string): string {
  return VALUE_LABELS[value] ?? value.charAt(0).toUpperCase() + value.slice(1);
}

/** The display labels for a Preset's set Variant tags, in axis order; the hand reads "Right hand". */
export function variantTagLabels(variant: VariantTags): string[] {
  return DIMENSIONS.flatMap((axis) => {
    const value = variant[axis];
    if (!value) return [];
    return [
      axis === "hand" ? `${axisOptionLabel(axis, value)} hand` : axisOptionLabel(axis, value),
    ];
  });
}

/** One declared axis of an edition: its key plus the distinct option tokens, first-seen order. */
export interface EditionAxis {
  key: AxisKey;
  options: string[];
}

/**
 * The per-edition axis declaration, derived by grouping the variant list (the list IS the
 * declaration, decision 0035): for each edition, the non-edition axes any of its variants set, with
 * their distinct option tokens in first-seen order, in canonical axis order. An edition that ships
 * only one option on an axis still declares it; a picker is free to hide single-option controls.
 */
export function editionAxes<T extends { variant: VariantTags }>(
  entries: readonly T[],
): Map<string, EditionAxis[]> {
  const groups = new Map<string, T[]>();
  for (const entry of entries) {
    const edition = entry.variant.edition;
    const group = groups.get(edition);
    if (group) group.push(entry);
    else groups.set(edition, [entry]);
  }
  const out = new Map<string, EditionAxis[]>();
  for (const [edition, group] of groups) {
    const axes: EditionAxis[] = [];
    for (const key of DIMENSIONS) {
      if (key === "edition") continue;
      const options: string[] = [];
      for (const entry of group) {
        const value = entry.variant[key];
        if (value && !options.includes(value)) options.push(value);
      }
      if (options.length > 0) axes.push({ key, options });
    }
    out.set(edition, axes);
  }
  return out;
}

const normalizeRoute = (routePath: string): string => routePath.toLowerCase().replace(/\/+$/, "");

/** The Preset at a canonical route, or undefined when no entry claims it (not found). */
export function resolveByRoute<T extends { routePath: string }>(
  entries: readonly T[],
  routePath: string,
): T | undefined {
  const target = normalizeRoute(routePath);
  return entries.find((entry) => normalizeRoute(entry.routePath) === target);
}

/** Entries matching every set Variant tag (an unset tag is unconstrained). */
export function facet<T extends { variant: VariantTags }>(
  entries: readonly T[],
  filter: Partial<VariantTags>,
): T[] {
  return entries.filter((entry) =>
    DIMENSIONS.every((dim) => filter[dim] === undefined || entry.variant[dim] === filter[dim]),
  );
}

/** The distinct values present per Variant-tag dimension, in first-seen order; absent axes stay empty. */
export function facetValues<T extends { variant: VariantTags }>(
  entries: readonly T[],
): Record<AxisKey, string[]> {
  const out: Record<AxisKey, string[]> = {
    edition: [],
    race: [],
    size: [],
    modifier: [],
    hand: [],
  };
  for (const entry of entries) {
    for (const dim of DIMENSIONS) {
      const value = entry.variant[dim];
      if (value && !out[dim].includes(value)) out[dim].push(value);
    }
  }
  return out;
}
